import {
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
  TitleSize,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { FormInsuranceCase } from '../../types';
import styles from './InsuranceCaseForm.module.css';
import { InsuranceProvider } from '@shared/types';
import { CollapsibleCard } from '../CollapsibleCard/CollapsibleCard';
import NonIncrementingNumberInput from '@/components/NonIncrementingNumberInput';

const InsuranceCaseForm = ({
  insuranceProvider,
  insuranceCase,
  insuranceCaseIndex,
  onChange,
  canDelete,
  onDelete,
}: {
  insuranceProvider: InsuranceProvider | null;
  insuranceCase: FormInsuranceCase;
  insuranceCaseIndex: number;
  onChange: (insuranceCase: FormInsuranceCase) => void;
  canDelete: boolean;
  onDelete: () => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(
    insuranceCase.firestoreId !== null,
  );

  return (
    <CollapsibleCard
      icon={null}
      title={
        <Title order={3} size='sm' c='dark'>
          {`Case ${insuranceCaseIndex + 1}`}
        </Title>
      }
      accessibilityLabel={`Case ${insuranceCaseIndex + 1}`}
      canDelete={canDelete}
      onDelete={onDelete}
      childrenWhenCollapsed={
        <CollapsedInsuranceCaseInfo insuranceCase={insuranceCase} />
      }
      childrenWhenExpanded={
        <ExpandedInsuranceCaseInfo
          insuranceProvider={insuranceProvider}
          insuranceCase={insuranceCase}
          onChange={onChange}
        />
      }
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
    />
  );
};

export default InsuranceCaseForm;

const ExpandedInsuranceCaseInfo = ({
  insuranceProvider,
  insuranceCase,
  onChange,
}: {
  insuranceProvider: InsuranceProvider | null;
  insuranceCase: FormInsuranceCase;
  onChange: (insuranceCase: FormInsuranceCase) => void;
}) => {
  const [caseId, setCaseId] = useState(insuranceCase.caseId || '');
  const [memberId, setMemberId] = useState(insuranceCase.memberId || '');
  const [caseValue, setCaseValue] = useState(insuranceCase.caseValue || '');
  const [caseType, setCaseType] = useState(insuranceCase.caseType || '');
  const [otherInsuranceProviderName, setOtherInsuranceProviderName] = useState(
    insuranceCase.otherInsuranceProviderName || '',
  );

  useEffect(() => {
    onChange({
      ...insuranceCase,
      caseId: caseId || null,
      memberId: memberId || null,
      caseValue: caseValue || null,
      caseType: caseType || null,
      otherInsuranceProviderName: otherInsuranceProviderName || null,
    });
  }, [caseId, memberId, caseValue, caseType, otherInsuranceProviderName]);

  const labelForCaseId =
    insuranceProvider === 'arag'
      ? 'CaseAssist Number'
      : insuranceProvider === 'metlife'
        ? 'Claim Number'
        : insuranceProvider === 'legalease'
          ? 'Case Number'
          : 'Case ID';
  const labelForMemberId =
    insuranceProvider === 'metlife' ? 'Eligibility ID' : 'Member ID';

  return (
    <Stack gap='lg' px='lg' pt='0' pb='md'>
      {insuranceProvider === 'other' && (
        <div className={styles.insuranceCaseSection}>
          <TextInput
            label='Provider Name'
            key={`${insuranceCase.tempId}-otherProvider`}
            value={otherInsuranceProviderName}
            onChange={(e) => setOtherInsuranceProviderName(e.target.value)}
            autoComplete='off'
            flex={'1 1 auto'}
          />
        </div>
      )}
      <div className={styles.insuranceCaseSection}>
        <TextInput
          label={labelForCaseId}
          key={`${insuranceCase.tempId}-caseId`}
          value={caseId}
          onChange={(e) => setCaseId(e.target.value)}
          autoComplete='off'
          flex={'1 1 auto'}
        />
        <TextInput
          label={labelForMemberId}
          key={`${insuranceCase.tempId}-memberId`}
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          autoComplete='off'
          flex={'1 1 auto'}
        />
      </div>
      <div className={styles.insuranceCaseSection}>
        <NonIncrementingNumberInput
          prefix='$'
          label='Case Value'
          key={`${insuranceCase.tempId}-caseValue`}
          value={caseValue}
          onChange={(value) => setCaseValue(value.toString())}
          flex={'1 1 auto'}
          decimalScale={2}
          min={0.0}
        />
        <TextInput
          label='Case Type'
          key={`${insuranceCase.tempId}-caseType`}
          value={caseType}
          onChange={(e) => setCaseType(e.target.value)}
          autoComplete='off'
          flex={'1 1 auto'}
          placeholder='e.g., Contract Dispute'
        />
      </div>
    </Stack>
  );
};

const CollapsedInsuranceCaseInfo = ({
  insuranceCase,
}: {
  insuranceCase: FormInsuranceCase;
}) => {
  const hasInfo =
    insuranceCase.caseId ||
    insuranceCase.memberId ||
    insuranceCase.caseValue ||
    insuranceCase.caseType;

  if (!hasInfo) {
    return (
      <Text pb='xs' pt='xs' size='sm' c='dimmed'>
        No case information
      </Text>
    );
  }

  return (
    <Stack gap='0.25rem'>
      {insuranceCase.caseId && (
        <Group gap='xs'>
          <Text size='sm' c='dimmed' fw={500}>
            Case ID:
          </Text>
          <Text size='sm' c='dimmed'>
            {insuranceCase.caseId}
          </Text>
        </Group>
      )}
      {insuranceCase.memberId && (
        <Group gap='xs'>
          <Text size='sm' c='dimmed' fw={500}>
            Member ID:
          </Text>
          <Text size='sm' c='dimmed'>
            {insuranceCase.memberId}
          </Text>
        </Group>
      )}
      {insuranceCase.caseValue && (
        <Group gap='xs'>
          <Text size='sm' c='dimmed' fw={500}>
            Value:
          </Text>
          <Text size='sm' c='dimmed'>
            ${insuranceCase.caseValue}
          </Text>
        </Group>
      )}
      {insuranceCase.caseType && (
        <Group gap='xs'>
          <Text size='sm' c='dimmed' fw={500}>
            Type:
          </Text>
          <Text size='sm' c='dimmed'>
            {insuranceCase.caseType}
          </Text>
        </Group>
      )}
    </Stack>
  );
};
